import React from 'react';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import { Link } from 'react-router-dom';
import { formatNumber } from '../../../../utils/utils';
import OrderChangeStatus from '../OrderChangeStatus';
import { OrderFilters } from '../../../../app/models/Order';
import OrdersFilter from '../GeneralOrderFilter';
import { Quote } from '../../../../app/models/Quote';

interface Props {
    orders: any[];
    totalRows: number;
    loading?: boolean;
    edit?: (id: number, type: 'transporte' | 'almacenamiento') => void;
    destroy?: (id: number) => void;
    show?: (id: number, type: 'transporte' | 'almacenamiento') => void;
    repeat?: (id: number) => void;
    documentQuote?: (id: number) => void;
    downloadExcel?: () => void;
    callbackSuccess: () => void;
    filter: OrderFilters;
    setFilter: (filter: OrderFilters) => void;
}

const OrdersUnifiedDataTable = ({
    orders,
    totalRows,
    loading = false,
    edit,
    destroy,
    show,
    repeat,
    documentQuote,
    downloadExcel,
    callbackSuccess,
    filter,
    setFilter,
}: Props) => {

    /**
     * Devuelve el ID legacy del pedido para navegar a las páginas de edición/visualización legacy.
     * Para nuevos pedidos sin legacy, usa el order_id directamente.
     */
    const getLegacyId = (row: any): number =>
        row.order_type === 'Transporte'
            ? (row.original_transportation_order_id ?? row.id)
            : (row.original_warehouse_order_id ?? row.id);

    const columns = [
        {
            name: 'Pedido',
            selector: (row: any) => row.order,
            sortable: true,
            sortField: 'id',
            width: '120px',
            cell: (row: any) => <span>{row.order}</span>,
        },
        {
            name: 'Sector',
            selector: (row: any) => row.sector_name,
            sortable: false,
            cell: (row: any) => <span>{row.sector_name ?? row.order_type}</span>,
        },
        {
            name: 'Oferta',
            selector: (row: any) =>
                row.transportation_quote_detail?.quote_id || row.warehouse_quote_detail?.quote_id,
            sortable: true,
            width: '120px',
            cell: (row: any) => {
                const quoteId =
                    row.transportation_quote_detail?.quote_id || row.warehouse_quote_detail?.quote_id;
                const position =
                    row.transportation_quote_detail?.position || row.warehouse_quote_detail?.position;
                return quoteId ? (
                    <div>
                        <Link to={`/quotes/${quoteId}/show`}>#{quoteId}</Link>
                        <br />
                        <small>Posición: {position ?? 'N/A'}</small>
                    </div>
                ) : null;
            },
        },
        {
            name: 'Sociedad',
            selector: (row: any) => row.society?.name,
            sortable: true,
            sortField: 'society_name',
            cell: (row: Quote) => <span>{row.society_name}</span>,
        },
        {
            name: 'Cliente',
            selector: (row: any) => row.client?.name,
            sortable: true,
            cell: (row: any) => <span>{row.client?.name || 'N/A'}</span>,
        },
        {
            name: <div className="w-100 text-center">Cantidad</div>,
            selector: (row: any) => row.quantity,
            sortable: false,
            cell: (row: any) => (
                <div className="w-100 text-end">
                    {row.quantity != null ? formatNumber(row.quantity) : null}
                </div>
            ),
        },
        {
            name: <div className="w-100 text-center">Unidad</div>,
            selector: (row: any) => row.quantity_unit,
            sortable: false,
            width: '150px',
            cell: (row: any) => (
                <div className="w-100 text-center">
                    {row.quantity_unit ?? null}
                </div>
            ),
        },
        {
            name: 'Estado',
            sortable: false,
            cell: (row: any) => (
                <OrderChangeStatus
                    orderId={row.id}
                    statuses={row.status_choices}
                    selectedStatus={row.status}
                    callbackSuccess={callbackSuccess}
                />
            ),
        },
        {
            name: 'Acciones',
            width: '210px',
            cell: (row: any) => (
                <div className="d-flex gap-1">
                    {documentQuote && row.order_type === 'Transporte' && (
                        <ButtonTableAction
                            callbackFunction={() => documentQuote(getLegacyId(row))}
                            classIcon={'mdi mdi-download'}
                            colorIcon={'text-success'}
                            title={'Descargar'}
                        />
                    )}
                    {repeat && row.order_type === 'Transporte' && (
                        <ButtonTableAction
                            callbackFunction={() => repeat(getLegacyId(row))}
                            classIcon={'mdi mdi-content-copy'}
                            colorIcon={'text-secondary'}
                            title={'Duplicar'}
                        />
                    )}
                    {show && (
                        <ButtonTableAction
                            callbackFunction={() =>
                                show(
                                    row.id,
                                    row.order_type === 'Transporte' ? 'transporte' : 'almacenamiento'
                                )
                            }
                            classIcon={'mdi mdi-eye'}
                            colorIcon={'text-info'}
                            title={'Ver'}
                        />
                    )}
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() =>
                                edit(
                                    row.id,
                                    row.order_type === 'Transporte' ? 'transporte' : 'almacenamiento'
                                )
                            }
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row.id)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="row mt-3">
            <div className="col">
                <OrdersFilter filter={filter} setFilter={setFilter} />
            </div>
            <div className="col-auto" style={{ paddingTop: 22 }}>
                {downloadExcel && (
                    <button className="btn btn-success btn-sm" onClick={downloadExcel}>
                        <i className="fa fa-file-excel" /> Descargar
                    </button>
                )}
            </div>
            <div className="col-12 mt-4">
                <DefaultDataTable
                    columns={columns}
                    data={orders}
                    progressPending={loading}
                    paginationTotalRows={totalRows}
                    filter={filter}
                    setFilter={setFilter}
                />
            </div>
        </div>
    );
};

export default OrdersUnifiedDataTable;
